import React, { useState, useRef } from "react";
import {
  X,
  Upload,
  FileText,
  Database,
  AlertCircle,
  Check,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Info,
  Type,
  FileCode,
  Zap,
  Brain,
  Tag,
  Wand2,
  CheckCircle,
  Layers,
  Edit3,
} from "lucide-react";
import useSmartDocument from "../../hooks/useSmartDocument";

const CreateDocumentModal = ({ databases, onClose, onSubmit }) => {
  const { fileToBase64, validatePDFFile } = useSmartDocument();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form data - 템플릿 이름 추가
  const [templateName, setTemplateName] = useState("");
  const [templateFile, setTemplateFile] = useState(null);
  const [templateBase64, setTemplateBase64] = useState("");
  const [selectedDatabases, setSelectedDatabases] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [sectionHints, setSectionHints] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef();

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect({ target: { files: [file] } });
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validatePDFFile(file);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setError("");
    setTemplateFile(file);

    try {
      const base64 = await fileToBase64(file);
      setTemplateBase64(base64);
    } catch (err) {
      setError("파일 변환 중 오류가 발생했습니다.");
      setTemplateFile(null);
      setTemplateBase64("");
    }
  };

  const handleDatabaseToggle = (databaseId) => {
    setSelectedDatabases((prev) => {
      if (prev.includes(databaseId)) {
        return prev.filter((id) => id !== databaseId);
      }
      return [...prev, databaseId];
    });
  };

  const handleSubmit = async () => {
    if (!templateName.trim()) {
      setError("문서 이름을 입력해주세요.");
      return;
    }
    if (!templateBase64) {
      setError("템플릿 파일을 업로드해주세요.");
      return;
    }
    if (selectedDatabases.length === 0) {
      setError("최소 하나의 지식 데이터베이스를 선택해주세요.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await onSubmit({
        templateName: templateName.trim(),
        templateBase64,
        instructions,
        sectionHints,
        documentIds: selectedDatabases,
        scope: "enterprise",
        targetFormat: "html",
        additionalData: {},
      });

      if (result.success) {
        onClose();
      } else {
        setError(result.error || "문서 생성에 실패했습니다.");
      }
    } catch (err) {
      setError("문서 생성 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-5">
      {/* 문서 이름 입력 */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
          <Type className="w-4 h-4 text-blue-600" />
          문서 이름
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="예: 2024년 마케팅 전략 보고서"
            className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            maxLength={100}
          />
          <Edit3 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        </div>
        <div className="flex justify-between items-center mt-2">
          <p className="text-xs text-gray-500">
            문서를 식별할 수 있는 명확한 이름을 입력하세요
          </p>
          <span className="text-xs text-gray-400">
            {templateName.length}/100
          </span>
        </div>
      </div>

      {/* 템플릿 파일 업로드 */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
          <FileText className="w-4 h-4 text-purple-600" />
          템플릿 파일
          <span className="text-red-500">*</span>
        </label>
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
            relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
            ${
              dragActive
                ? "border-blue-500 bg-blue-50"
                : templateFile
                ? "border-green-400 bg-green-50"
                : "border-gray-300 hover:border-gray-400 bg-gray-50/50"
            }
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            className="hidden"
          />

          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 1px)`,
                backgroundSize: "20px 20px",
              }}
            ></div>
          </div>

          {templateFile ? (
            <div className="relative space-y-3">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {templateFile.name}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {(templateFile.size / 1024 / 1024).toFixed(2)} MB • PDF 문서
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setTemplateFile(null);
                  setTemplateBase64("");
                }}
                className="text-xs text-red-600 hover:text-red-700 font-medium"
              >
                다른 파일 선택
              </button>
            </div>
          ) : (
            <div className="relative space-y-3">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center">
                <Upload
                  className={`w-8 h-8 ${
                    dragActive ? "text-blue-600" : "text-gray-400"
                  }`}
                />
              </div>
              <div>
                <p className="text-sm text-gray-700 font-medium">
                  {dragActive
                    ? "파일을 놓으세요"
                    : "클릭하거나 파일을 드래그하세요"}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  PDF 파일 (최대 15MB)
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 작성 지침 */}
      <div>
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
          <Wand2 className="w-4 h-4 text-indigo-600" />
          AI 작성 지침
          <span className="text-xs text-gray-400 font-normal">(선택사항)</span>
        </label>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="AI가 문서 작성 시 참고할 특별한 지침이나 요구사항을 입력하세요..."
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none"
          rows={4}
        />
        <p className="text-xs text-gray-500 mt-2">
          예: "전문적이고 간결한 톤으로 작성", "구체적인 수치와 데이터 포함"
        </p>
      </div>

      {/* Advanced Options */}
      <div className="border-t border-gray-100 pt-4">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
        >
          {showAdvanced ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
          고급 옵션
        </button>
        {showAdvanced && (
          <div className="mt-4 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
              <Layers className="w-4 h-4" />
              섹션별 힌트
            </label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="섹션 이름 입력 후 Enter"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.value) {
                      setSectionHints((prev) => ({
                        ...prev,
                        [e.target.value]: "",
                      }));
                      e.target.value = "";
                    }
                  }}
                />
              </div>
              {Object.entries(sectionHints).map(([section, hint]) => (
                <div
                  key={section}
                  className="flex gap-2 items-center bg-white p-2 rounded-lg"
                >
                  <Tag className="w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={section}
                    disabled
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50"
                  />
                  <input
                    type="text"
                    value={hint}
                    onChange={(e) =>
                      setSectionHints((prev) => ({
                        ...prev,
                        [section]: e.target.value,
                      }))
                    }
                    placeholder="이 섹션에 대한 힌트..."
                    className="flex-2 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => {
                      setSectionHints((prev) => {
                        const newHints = { ...prev };
                        delete newHints[section];
                        return newHints;
                      });
                    }}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Database className="w-4 h-4 text-purple-600" />
            지식 데이터베이스 선택
            <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
            <Info className="w-3 h-3" />
            AI가 참고할 데이터 소스
          </div>
        </div>

        {databases.length === 0 ? (
          <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
            <Database className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-600 font-medium">
              사용 가능한 지식 데이터베이스가 없습니다
            </p>
            <p className="text-sm text-gray-500 mt-2">
              먼저 지식 데이터베이스를 생성해주세요
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 max-h-96 overflow-y-auto pr-2">
              {databases.map((db) => (
                <div
                  key={db.database_id}
                  onClick={() => handleDatabaseToggle(db.database_id)}
                  className={`
                    relative p-4 rounded-xl border-2 cursor-pointer transition-all transform
                    ${
                      selectedDatabases.includes(db.database_id)
                        ? "border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg scale-[1.02]"
                        : "border-gray-200 hover:border-gray-300 bg-white hover:shadow-md"
                    }
                  `}
                >
                  {/* Selection indicator */}
                  <div className="absolute -top-2 -right-2">
                    <div
                      className={`
                        w-6 h-6 rounded-full flex items-center justify-center transition-all
                        ${
                          selectedDatabases.includes(db.database_id)
                            ? "bg-blue-500 scale-100"
                            : "bg-gray-200 scale-0"
                        }
                      `}
                    >
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div
                      className={`
                        w-10 h-10 rounded-xl flex items-center justify-center transition-all
                        ${
                          selectedDatabases.includes(db.database_id)
                            ? "bg-gradient-to-br from-blue-500 to-indigo-600"
                            : "bg-gray-100"
                        }
                      `}
                    >
                      <Database
                        className={`w-5 h-5 ${
                          selectedDatabases.includes(db.database_id)
                            ? "text-white"
                            : "text-gray-500"
                        }`}
                      />
                    </div>

                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        {db.database_name}
                        {db.shared_by && (
                          <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                            공유됨
                          </span>
                        )}
                      </h3>
                      {db.database_description && (
                        <p className="text-sm text-gray-600 mt-1">
                          {db.database_description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-xs">
                        <span className="text-gray-500 flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {db.typeLabel}
                        </span>
                        <span className="text-gray-500 flex items-center gap-1">
                          <Layers className="w-3 h-3" />
                          {db.chunk_size} 청크
                        </span>
                        {db.shared_by && (
                          <span className="text-gray-500">
                            제공: {db.shared_by}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">
                  선택된 데이터베이스: {selectedDatabases.length}개
                </span>
              </div>
              {selectedDatabases.length > 0 && (
                <span className="text-xs text-blue-600">
                  AI가 더 정확한 콘텐츠를 생성합니다
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="relative px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  새 스마트 문서 생성
                </h2>
                <p className="text-xs text-gray-600 mt-0.5">
                  AI와 함께 지능형 문서를 작성하세요
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-center space-x-4">
            <div
              className={`flex items-center gap-3 ${
                step >= 1 ? "text-blue-600" : "text-gray-400"
              }`}
            >
              <div
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all
                  ${
                    step >= 1
                      ? "bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg"
                      : "bg-gray-200 text-gray-500"
                  }
                `}
              >
                1
              </div>
              <div>
                <span className="text-sm font-semibold">기본 정보</span>
                <span className="text-xs text-gray-500 block">
                  이름 및 템플릿
                </span>
              </div>
            </div>

            <div
              className={`flex-1 h-1 rounded-full ${
                step >= 2
                  ? "bg-gradient-to-r from-blue-500 to-indigo-500"
                  : "bg-gray-200"
              }`}
            />

            <div
              className={`flex items-center gap-3 ${
                step >= 2 ? "text-blue-600" : "text-gray-400"
              }`}
            >
              <div
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all
                  ${
                    step >= 2
                      ? "bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg"
                      : "bg-gray-200 text-gray-500"
                  }
                `}
              >
                2
              </div>
              <div>
                <span className="text-sm font-semibold">지식 DB</span>
                <span className="text-xs text-gray-500 block">데이터 선택</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          className="px-6 py-6 overflow-y-auto"
          style={{ maxHeight: "calc(90vh - 280px)" }}
        >
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          )}

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-xl transition-all"
            >
              취소
            </button>
            <div className="flex items-center gap-3">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  disabled={loading}
                  className="px-5 py-2.5 text-gray-700 font-medium border border-gray-300 rounded-xl hover:bg-gray-100 transition-all disabled:opacity-50"
                >
                  이전
                </button>
              )}
              {step < 2 ? (
                <button
                  onClick={() => {
                    if (!templateName.trim()) {
                      setError("문서 이름을 입력해주세요.");
                      return;
                    }
                    if (!templateBase64) {
                      setError("템플릿 파일을 업로드해주세요.");
                      return;
                    }
                    setError("");
                    setStep(step + 1);
                  }}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg flex items-center gap-2"
                >
                  다음 단계
                  <ChevronDown className="w-4 h-4 rotate-270" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading || selectedDatabases.length === 0}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      생성 중...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      문서 생성
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateDocumentModal;
